import React, { ComponentProps, useCallback } from 'react'
import { HStack, VStack } from '@musinsa/mwms-ds'
import { Radio } from '@/components/content/Radio'
import Input from '@/components/content/Input'
import RadioGroup from '@/components/content/RadioGroup'
import { FormField } from '@/components/content/FormField'
import styled from 'styled-components'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'
import { UseFormReturn, Controller } from 'react-hook-form'
import { ShipperForm } from '@/services/shipper/schemas'

interface Props {
  form: UseFormReturn<ShipperForm>
  type: 'create' | 'update'
  onLogisticsServiceTypeChange?: (value: string) => void
}

function BaseShipperForm({ form, onLogisticsServiceTypeChange, type }: Props) {
  const { control, watch } = form

  const logisticsServiceType = watch('logisticsServiceType')
  const shipperCode = watch('shipperCode')

  const onChangeLogisticsServiceType = (value: string) => {
    onLogisticsServiceTypeChange?.(value)
    form.reset()
  }

  return (
    <div>
      {type === 'create' ? (
        <FormField label="화주 유형" required>
          <RadioGroup direction="row">
            <Radio value="MWP" checked={logisticsServiceType === 'MWP'} onChange={onChangeLogisticsServiceType} label="MWP" />
            <Radio value="MLT" checked={logisticsServiceType === 'MLT'} onChange={onChangeLogisticsServiceType} label="MLT" />
            <Radio value="MCD" checked={logisticsServiceType === 'MCD'} onChange={onChangeLogisticsServiceType} label="MCD" />
          </RadioGroup>
        </FormField>
      ) : (
        <HStack>
          <FormField label="화주 유형">
            <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
              {logisticsServiceType}
            </Text>
          </FormField>
          <FormField label="화주 코드">
            <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
              {shipperCode}
            </Text>
          </FormField>
        </HStack>
      )}

      <FormField label="화주명" required>
        <Controller
          name="shipperName"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="화주명 입력" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>

      <FormField label="상호명" required>
        <Controller
          name="companyName"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="상호명 입력" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>

      <FormField label="사업자등록번호" required>
        <Controller
          name="businessRegistrationNumber"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="사업자등록번호 입력" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>

      <FormField label="대표자명" required>
        <Controller
          name="ownerName"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="대표자명 입력" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>

      <FormField label="업태">
        <Controller
          name="businessType"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="업태 입력 (ex.소매업)" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>

      <FormField label="업종">
        <Controller
          name="businessMajor"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="업종 입력 (ex.의류)" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>

      <FormField label="주소">
        <VStack gap={8}>
          <Controller
            name="zipCode"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <FixedWidthInput {...field} placeholder="우편번호 입력" value={field.value || ''} />
                {fieldState.error && (
                  <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                    {fieldState.error.message}
                  </ErrorMessage>
                )}
              </>
            )}
          />
          <Controller
            name="address"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Input {...field} placeholder="상세주소 입력" value={field.value || ''} />
                {fieldState.error && (
                  <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                    {fieldState.error.message}
                  </ErrorMessage>
                )}
              </>
            )}
          />
        </VStack>
      </FormField>

      <FormField label="전화번호">
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input {...field} placeholder="전화번호 입력 (- 포함)" value={field.value || ''} />
              {fieldState.error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {fieldState.error.message}
                </ErrorMessage>
              )}
            </>
          )}
        />
      </FormField>
      {type === 'update' && (
        <Text kind="Body_12_medium" color={colorSystem.text.common.tertiary} style={{ marginTop: '12px' }}>
          * 대표자명, 업태, 업종, 주소, 전화번호, 상호명 변경 시, 같은 사업자등록번호를 가진 모든 화주사에 동일하게 반영됩니다.
        </Text>
      )}
    </div>
  )
}

export default BaseShipperForm

const ErrorMessage = styled(Text)`
  margin-top: 2px;
  margin-left: 4px;
`

const FixedWidthInput = styled(Input)`
  width: 240px;
`
